// Rozpoznávání řeči: bonus k nahrávce, ne podmínka. V prohlížeči Web Speech API, v nativní aplikaci
// plugin @capacitor-community/speech-recognition (systémové rozpoznávání telefonu).
// Text ze zařízení jde na server jako `deviceText`; bez něj přepíše nahrávku server.
// Nativní plugin se načítá dynamicky až za isNative(), takže do webového bundlu nejde.
import { isNative } from './native'

// Vrací modul, ne samotný plugin: proxy pluginu v Capacitoru nesmí projít přes await/then.
const importSpeechModule = () => import('@capacitor-community/speech-recognition')
type SpeechModule = Awaited<ReturnType<typeof importSpeechModule>>
let speechModule: SpeechModule | null = null

async function loadSpeechModule(): Promise<SpeechModule> {
  speechModule ??= await importSpeechModule()
  return speechModule
}

interface RecognitionResult {
  readonly isFinal: boolean
  readonly length: number
  [index: number]: { transcript: string }
}
interface RecognitionEvent {
  readonly resultIndex: number
  readonly results: { readonly length: number; [index: number]: RecognitionResult }
}
interface Recognition {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((event: RecognitionEvent) => void) | null
  onerror: ((event: { error?: string }) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
}
type RecognitionCtor = new () => Recognition

function recognitionCtor(): RecognitionCtor | undefined {
  const g = globalThis as {
    SpeechRecognition?: RecognitionCtor
    webkitSpeechRecognition?: RecognitionCtor
  }
  return g.SpeechRecognition ?? g.webkitSpeechRecognition
}

export interface Speech {
  isSupported(): boolean
  /** Spustí rozpoznávání; `onText` dostane hotový text a rozpracovaný konec. */
  start(onText: (finalText: string, interimText: string) => void): void
  /** Zastaví rozpoznávání a vrátí dosavadní text (včetně rozpracovaného konce). */
  stop(): string
}

const FATAL_ERRORS = new Set(['not-allowed', 'service-not-allowed', 'language-not-supported'])

function join(a: string, b: string): string {
  return [a, b]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' ')
}

export function createSpeech(): Speech {
  return isNative() ? createNativeSpeech() : createWebSpeech()
}

/** Po jak dlouhé pauze se nativní rozpoznávání po skončení věty znovu spustí. */
export const NATIVE_RESTART_DELAY_MS = 250
/** Kolik prázdných cyklů po sobě (ticho) se ještě obnovuje, pak se rozpoznávání vzdá. */
export const NATIVE_MAX_EMPTY_CYCLES = 3

const NATIVE_OPTIONS = { language: 'cs-CZ', partialResults: true, popup: false } as const

/**
 * Nativní rozpoznávání: plugin hlásí průběžný text (partialResults) a po ticho se sám zastaví,
 * proto se po skončení věty text uloží a rozpoznávání se znovu spustí, dokud nahráváme.
 * Jakákoli chyba (oprávnění, nedostupná služba) jen vypne rozpoznávání; hlasovku pak přepíše server.
 */
export function createNativeSpeech(): Speech {
  let active = false
  let session = 0
  let finalText = ''
  let interimText = ''
  let emptyCycles = 0
  let restartTimer: ReturnType<typeof setTimeout> | null = null

  function isSupported(): boolean {
    return true
  }

  async function begin(
    id: number,
    onText: (finalText: string, interimText: string) => void,
  ): Promise<void> {
    try {
      const { SpeechRecognition } = await loadSpeechModule()
      if (!(await SpeechRecognition.available()).available) throw new Error('nedostupné')
      let perm = await SpeechRecognition.checkPermissions()
      if (perm.speechRecognition !== 'granted') perm = await SpeechRecognition.requestPermissions()
      if (perm.speechRecognition !== 'granted') throw new Error('oprávnění')
      if (!active || id !== session) return

      await SpeechRecognition.addListener('partialResults', (data) => {
        if (!active || id !== session) return
        interimText = data.matches?.[0] ?? ''
        if (interimText) emptyCycles = 0
        onText(finalText, interimText)
      })
      await SpeechRecognition.addListener('listeningState', (data) => {
        if (data.status !== 'stopped' || !active || id !== session) return
        // Věta skončila: rozpracovaný text je hotový, navážeme další.
        if (interimText) {
          finalText = join(finalText, interimText)
          interimText = ''
          onText(finalText, interimText)
        } else {
          emptyCycles++
        }
        if (emptyCycles >= NATIVE_MAX_EMPTY_CYCLES) {
          active = false
          return
        }
        restartTimer = setTimeout(() => {
          restartTimer = null
          if (active && id === session) void listen(id)
        }, NATIVE_RESTART_DELAY_MS)
      })
      await listen(id)
    } catch (e) {
      console.warn('[speech] nativní rozpoznávání se nespustilo', e)
      if (id === session) active = false
    }
  }

  async function listen(id: number): Promise<void> {
    try {
      const { SpeechRecognition } = await loadSpeechModule()
      await SpeechRecognition.start({ ...NATIVE_OPTIONS })
    } catch (e) {
      console.warn('[speech] nativní rozpoznávání se nepodařilo spustit', e)
      if (id === session) active = false
    }
  }

  function start(onText: (finalText: string, interimText: string) => void): void {
    if (active) return
    active = true
    session++
    finalText = ''
    interimText = ''
    emptyCycles = 0
    void begin(session, onText)
  }

  function stop(): string {
    const wasActive = active
    active = false
    session++
    if (restartTimer) clearTimeout(restartTimer)
    restartTimer = null
    // Modul je po startu načtený; když ne, nic se nespustilo a není co zastavovat.
    if (speechModule) {
      const { SpeechRecognition } = speechModule
      if (wasActive) void SpeechRecognition.stop().catch(() => undefined)
      void SpeechRecognition.removeAllListeners().catch(() => undefined)
    }
    const text = join(finalText, interimText)
    finalText = ''
    interimText = ''
    return text
  }

  return { isSupported, start, stop }
}

function createWebSpeech(): Speech {
  let recognition: Recognition | null = null
  let active = false
  let finalText = ''
  let interimText = ''

  function isSupported(): boolean {
    return recognitionCtor() !== undefined
  }

  function start(onText: (finalText: string, interimText: string) => void): void {
    const Ctor = recognitionCtor()
    if (!Ctor || active) return
    finalText = ''
    interimText = ''
    let rec: Recognition
    try {
      rec = new Ctor()
      rec.lang = 'cs-CZ'
      rec.continuous = true
      rec.interimResults = true
    } catch (e) {
      console.warn('[speech] rozpoznávání nejde vytvořit', e)
      return
    }
    recognition = rec
    active = true

    rec.onresult = (event) => {
      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const text = result?.[0]?.transcript ?? ''
        if (result?.isFinal) finalText = join(finalText, text)
        else interim = join(interim, text)
      }
      interimText = interim
      onText(finalText, interimText)
    }
    rec.onerror = (event) => {
      console.warn('[speech] chyba rozpoznávání', event.error)
      if (event.error && FATAL_ERRORS.has(event.error)) active = false
    }
    rec.onend = () => {
      // Prohlížeč po delším tichu rozpoznávání sám ukončí; dokud nahráváme, navážeme.
      if (!active) return
      try {
        rec.start()
      } catch (e) {
        console.warn('[speech] rozpoznávání se nepodařilo obnovit', e)
        active = false
      }
    }
    try {
      rec.start()
    } catch (e) {
      console.warn('[speech] rozpoznávání se nepodařilo spustit', e)
      active = false
    }
  }

  function stop(): string {
    const rec = recognition
    active = false
    recognition = null
    if (rec) {
      rec.onresult = null
      rec.onend = null
      try {
        rec.stop()
      } catch {
        /* už je zastavené */
      }
    }
    const text = join(finalText, interimText)
    finalText = ''
    interimText = ''
    return text
  }

  return { isSupported, start, stop }
}
